import { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react';
import PhotoCredit from '../../ui/PhotoCredit/PhotoCredit';
import { useHasMounted } from '../../../hooks/useHasMounted';
import type { TitledText } from '../../../locales/types';
import styles from './PlaceBanner.module.css';

interface PlaceBannerProps {
    image: string;
    width: number;
    height: number;
    alt: string;
    eyebrow: string;
    title: string;
    caption: string;
    facts: TitledText[];
    credit: string;
    creditHref: string;
}

interface BannerImageProps {
    image: string;
    width: number;
    height: number;
    alt: string;
}

const DRIFT = ['-16%', '0%'];

function BannerImage({ image, width, height, alt }: BannerImageProps) {
    return <img src={image} alt={alt} width={width} height={height} loading="lazy" decoding="async" className={styles.image} />;
}

function DriftingBannerImage(props: BannerImageProps) {
    const ref = useRef<HTMLDivElement>(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
    const y = useTransform(scrollYProgress, [0, 1], DRIFT);

    return (
        <motion.div ref={ref} className={styles.drift} style={{ y }}>
            <BannerImage {...props} />
        </motion.div>
    );
}

export default function PlaceBanner({ image, width, height, alt, eyebrow, title, caption, facts, credit, creditHref }: PlaceBannerProps) {
    const hasMounted = useHasMounted();
    const prefersReducedMotion = useReducedMotion();
    const drifts = hasMounted && !prefersReducedMotion;

    return (
        <figure className={styles.banner}>
            <div className={styles.window}>
                {drifts ? <DriftingBannerImage image={image} width={width} height={height} alt={alt} /> : <div className={styles.drift}><BannerImage image={image} width={width} height={height} alt={alt} /></div>}
                <div className={`page-inner ${styles.overlay}`}>
                    <span className={styles.eyebrow}>{eyebrow}</span>
                    <span className={styles.title}>{title}</span>
                </div>
                <PhotoCredit source={credit} href={creditHref} />
            </div>
            <div className={`page-inner ${styles.below}`}>
                <figcaption className={styles.caption}>{caption}</figcaption>
                <dl className={styles.facts}>
                    {facts.map((fact) => (
                        <div key={fact.title} className={styles.fact}>
                            <dt>{fact.title}</dt>
                            <dd>{fact.body}</dd>
                        </div>
                    ))}
                </dl>
            </div>
        </figure>
    );
}
